import { useId, useLayoutEffect, useState } from "react";
import { LoaderCircle, Trash2 } from "lucide-react";
import type { AdminMap } from "../shared/types";
import { ReviewDialog } from "./MapReviewDialogs";
import { Notice } from "./ui";
import "./map-delete.css";

export function DeleteMapDialog({
  map,
  hasUnsavedChanges,
  returnFocusTo,
  busy,
  error,
  stale,
  onConfirm,
  onClose,
}: {
  map: AdminMap;
  hasUnsavedChanges: boolean;
  returnFocusTo: HTMLElement | null;
  busy: boolean;
  error: string;
  stale: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [confirmation, setConfirmation] = useState("");
  const descriptionId = useId();
  useLayoutEffect(() => {
    if (stale) setConfirmation("");
  }, [stale]);
  const canConfirm = confirmation === map.id && !busy && !stale;
  return (
    <ReviewDialog
      title="Delete map?"
      description="Remove this map from the workspace. This cannot be undone in Gem."
      busy={busy}
      returnFocusTo={returnFocusTo}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="em-button em-button-secondary"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="em-button em-map-delete-confirm"
            disabled={!canConfirm}
            onClick={() => {
              if (canConfirm) onConfirm();
            }}
          >
            {busy ? (
              <LoaderCircle size={16} className="em-spin" aria-hidden="true" />
            ) : (
              <Trash2 size={16} aria-hidden="true" />
            )}
            {busy ? "Deleting…" : "Delete map"}
          </button>
        </>
      }
    >
      {error && <Notice>{error}</Notice>}
      <div className="em-map-delete-summary">
        <strong>{map.title}</strong>
        <code>{map.id}</code>
      </div>
      {map.publishedRevision !== null && (
        <p className="em-map-delete-description">
          Its public preview and embeds will no longer load. Cached copies can
          take up to a minute to expire; maps already open may remain visible.
        </p>
      )}
      {hasUnsavedChanges && (
        <p className="em-map-delete-description">
          Your unsaved changes will also be discarded.
        </p>
      )}
      <label className="em-map-delete-field gau-scope">
        Map ID to confirm
        <input
          type="text"
          className="gau-input"
          value={confirmation}
          disabled={busy || stale}
          autoComplete="off"
          autoCapitalize="none"
          spellcheck={false}
          aria-describedby={descriptionId}
          onChange={(event) => setConfirmation(event.currentTarget.value)}
        />
      </label>
      <p id={descriptionId} className="em-map-delete-description">
        Type <code>{map.id}</code> exactly to confirm.
      </p>
    </ReviewDialog>
  );
}
